import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Components
import Header from './Header';
import Footer from './Footer';
import OnboardingModal from './OnboardingModal';
import ScrollToTop from './ScrollToTop';
import useOnboarding from '../hooks/useOnboarding';

// Lazy loaded pages
const Home = React.lazy(() => import('../pages/Home'));
const Compare = React.lazy(() => import('../pages/Compare'));
const Equipment = React.lazy(() => import('../pages/Equipment'));
const EquipmentDetail = React.lazy(() => import('../pages/EquipmentDetail'));
const Articles = React.lazy(() => import('../pages/Articles'));
const ArticleDetail = React.lazy(() => import('../pages/ArticleDetail'));
const Admin = React.lazy(() => import('../pages/Admin'));
const MyStudio = React.lazy(() => import('./MyStudio'));
const UserDashboard = React.lazy(() => import('../pages/UserDashboard'));
const PaymentSuccess = React.lazy(() => import('../pages/PaymentSuccess'));
const StudioListings = React.lazy(() => import('../pages/StudioListings'));
const StudioDetail = React.lazy(() => import('../pages/StudioDetail'));
const BrandDetail = React.lazy(() => import('../pages/BrandDetail'));
const Brands = React.lazy(() => import('../pages/Brands'));
const Support = React.lazy(() => import('../pages/Support'));
const Contact = React.lazy(() => import('../pages/Contact'));
const PrivacyPolicy = React.lazy(() => import('../pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('../pages/TermsOfService'));
const BetaLogin = React.lazy(() => import('../pages/BetaLogin'));
const NotFound = React.lazy(() => import('../pages/NotFound'));

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
`;

const MainContent = styled.div`
  flex: 1;
  padding-top: 80px; /* Account for fixed header */
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #ff6b35;
  font-size: 1.2rem;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 107, 53, 0.3);
  border-top: 3px solid #ff6b35;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PageLoadingFallback = () => (
  <LoadingContainer>
    <Spinner />
    <LoadingSpinner>Loading page...</LoadingSpinner>
  </LoadingContainer>
);

// Error boundary for chunk loading errors
const ChunkErrorHandler = ({ children }) => {
  React.useEffect(() => {
    const handleChunkError = (error) => {
      if (error && (
        error.message?.includes('Loading chunk') ||
        error.message?.includes('ChunkLoadError') ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Unexpected token')
      )) {
        console.warn('Chunk loading error detected, reloading page...');
        // Clear caches and reload
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => {
              caches.delete(name);
            });
          });
        }
        window.location.reload();
      }
    };

    window.addEventListener('error', (e) => {
      if (e.filename && e.filename.includes('chunk.js')) {
        handleChunkError(e);
      }
    });

    window.addEventListener('unhandledrejection', (e) => {
      handleChunkError(e.reason);
    });

    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleChunkError);
    };
  }, []);

  return children;
};

const AppWithOnboarding = () => {
  const { showOnboarding, loading, completeOnboarding } = useOnboarding();

  if (loading) {
    return (
      <AppContainer>
        <LoadingContainer>
          <Spinner />
          <LoadingSpinner>Loading application...</LoadingSpinner>
        </LoadingContainer>
      </AppContainer>
    );
  }

  return (
    <ChunkErrorHandler>
      <AppContainer>
        <ScrollToTop />
        <Header />
        <MainContent>
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/equipment" element={<Equipment />} />
            <Route path="/equipment/:slug" element={<EquipmentDetail />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<ArticleDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/my-studio" element={<MyStudio />} />
            <Route path="/user-dashboard" element={<UserDashboard />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/studios" element={<StudioListings />} />
            <Route path="/studio/:userId" element={<StudioDetail />} />
            <Route path="/brands" element={<Brands />} />
            <Route path="/brand/:slug" element={<BrandDetail />} />
            <Route path="/support" element={<Support />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/beta-login" element={<BetaLogin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </MainContent>
      <Footer />
      
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => {}}
        onComplete={completeOnboarding}
      />
      </AppContainer>
    </ChunkErrorHandler>
  );
};

export default AppWithOnboarding;
